import {
  ActionIcon,
  Alert,
  AppShell,
  Avatar,
  Burger,
  Center,
  Group,
  Loader,
  Menu,
  NavLink as MantineNavLink,
  Select,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHistory,
  IconInbox,
  IconLogout,
  IconMoon,
  IconPhoneCall,
  IconSettings,
  IconShieldCheck,
  IconSun,
  IconTargetArrow,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthActions } from '../auth/AuthGate.js';
import { api, ApiError } from '../lib/api.js';
import { ImpersonationBanner } from '../components/ImpersonationBanner.js';
import { Logo } from '../components/Logo.js';

/**
 * The dealership-facing shell.
 *
 * Deliberately not the admin console with things hidden. A dealership sees a
 * small, plainly-worded area about their own store — no talk of watermarks,
 * providers or environments, and no sign that other dealerships exist.
 */

export interface PortalMe {
  email: string;
  role: string;
  permissions: string[];
  dealerships: { id: string; name: string }[];
  /** Set when platform staff are borrowing this dealership's access. */
  impersonating?: string | null;
}

const NAV = [
  { to: '/portal', label: 'Your dealership', icon: IconShieldCheck, end: true, permission: 'dealers:read' },
  { to: '/portal/calls', label: 'Calls', icon: IconPhoneCall, permission: 'leads:read' },
  { to: '/portal/leads', label: 'Enquiries', icon: IconInbox, permission: 'leads:read' },
  { to: '/portal/opportunities', label: 'Opportunities', icon: IconTargetArrow, permission: 'leads:read' },
  { to: '/portal/settings', label: 'Settings', icon: IconSettings, permission: 'dealers:config' },
  { to: '/portal/activity', label: 'History', icon: IconHistory, permission: 'activity:read' },
];

export function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure();
  const { signOutProvider } = useAuthActions();
  const [params, setParams] = useSearchParams();
  // Computed, not stored: the stored scheme defaults to 'auto', which would
  // never read as dark. See AdminLayout.
  const { setColorScheme } = useMantineColorScheme();
  const isDark = useComputedColorScheme('light', { getInitialValueInEffect: true }) === 'dark';

  const me = useQuery({
    queryKey: ['portal-me'],
    queryFn: () => api.get<PortalMe>('/admin/portal/me'),
    retry: false,
  });

  if (me.isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  if (me.isError) {
    const err = me.error as ApiError;
    if (err.status === 401) {
      navigate('/login', { replace: true });
      return null;
    }
    // 403 here means platform staff wandered in. Point them at the console
    // rather than showing an error they cannot act on.
    return (
      <Center mih="100vh" p="md">
        <Alert color="gray" variant="light" maw={460}>
          <Text size="sm" fw={600} mb={4}>
            This area is for dealership accounts
          </Text>
          <Text size="sm">
            {err.message}{' '}
            <UnstyledButton
              onClick={() => navigate('/app')}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Open the console
            </UnstyledButton>
          </Text>
        </Alert>
      </Center>
    );
  }

  async function signOut() {
    await api.post('/admin/logout').catch(() => {});
    await signOutProvider();
    navigate('/login', { replace: true });
  }

  const held = me.data?.permissions ?? [];
  const nav = NAV.filter((i) => !i.permission || held.includes(i.permission));
  const stores = me.data?.dealerships ?? [];
  const current = params.get('dealerId') ?? stores[0]?.id ?? '';
  const email = me.data?.email ?? '';

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 230, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menu" />
            <Logo height={22} />
          </Group>

          <Group gap="sm">
            <Tooltip label={isDark ? 'Light mode' : 'Dark mode'}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                radius="md"
                onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <IconSun size={18} stroke={1.7} /> : <IconMoon size={18} stroke={1.7} />}
              </ActionIcon>
            </Tooltip>

            {/* Only shown to someone who actually manages more than one store. */}
            {stores.length > 1 && (
              <Select
                size="xs"
                w={220}
                data={stores.map((s) => ({ value: s.id, label: s.name }))}
                value={current}
                onChange={(v) => {
                  if (v) setParams({ dealerId: v });
                }}
                allowDeselect={false}
                aria-label="Choose dealership"
              />
            )}

            <Menu position="bottom-end" width={220} withArrow>
              <Menu.Target>
                <UnstyledButton aria-label="Account menu">
                  <Avatar size={30} radius="xl" color="brand" variant="filled">
                    {email.slice(0, 1).toUpperCase() || '?'}
                  </Avatar>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="xs" truncate>
                    {email}
                  </Text>
                </Menu.Label>
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={15} />}
                  onClick={() => void signOut()}
                >
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        {nav.map((item) => (
          <MantineNavLink
            key={item.to}
            component={NavLink}
            // Carry the chosen dealership across navigation, or a multi-store
            // user silently jumps back to their first one on every click.
            to={current ? `${item.to}?dealerId=${current}` : item.to}
            end={item.end}
            label={item.label}
            leftSection={<item.icon size={18} />}
            active={
              item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to)
            }
            onClick={close}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        {/* The portal is the usual reason to impersonate, so the reminder has
            to be here too — otherwise a developer reads a dealership's page
            believing it is their own view. */}
        {me.data?.impersonating && <ImpersonationBanner target={me.data.impersonating} />}
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
